import { Ref, useCallback, useImperativeHandle } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import {
  isLoading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { ItemNoAndType } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { AttachArticle } from "../../../AttachArticle/AttachArticle";
import { useAllAttachedItems } from "@coworker/apprestructured/src/task-management/services/useAllAttachedItems";
import { useResetFields } from "@coworker/apprestructured/src/task-management/services/useResetFields";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import {
  shouldShowField,
  VisibilityCondition,
} from "@coworker/apprestructured/src/task-management/helpers/visibilityCondition.helper";
import { useTranslation } from "@coworker/locales";

export type ChangeArticleCreatorFragmentConfigProps = {
  field: string; // Will be "details.new_item"
  title: string;
  resetFieldsOnChange: string[];
  visibility: VisibilityCondition;
};

type ChangeArticleCreatorFragmentProps = {
  configProps: ChangeArticleCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

const NO_ITEM_NOS: ItemNoAndType[] = [];

function ChangeArticleCreatorFragment({
  configProps,
  commonProps,
  ref,
}: ChangeArticleCreatorFragmentProps) {
  const { field, title, resetFieldsOnChange, visibility } = configProps;
  const { blockInput, task, onTaskChange, showValidation, taskType } =
    commonProps;
  const { t } = useTranslation();

  // Level 1: Schema validation — does the data have the right shape?
  const itemNoAndType = validateInitialDetailsFieldValue<ItemNoAndType>(
    getByPath(task, field),
    field,
    taskType
  );
  // Level 2: Business validation — does the item actually exist?
  const { items, isFetchingItems } = useAllAttachedItems(
    isLoading(itemNoAndType)
      ? LOADING
      : itemNoAndType === null
        ? NO_ITEM_NOS
        : [itemNoAndType]
  );

  const resetFields = useResetFields(taskType);

  // Get existing item to compare
  const existingItemNoAndType = isLoading(task)
    ? LOADING
    : validateInitialDetailsFieldValue<ItemNoAndType>(
        getByPath(task, "details.item"),
        "details.item",
        taskType
      );

  const handleItemChange = useCallback(
    (newItemNoAndType: ItemNoAndType) => {
      const { no, type } = newItemNoAndType;
      onTaskChange(
        setByPath(field, { no, type }, resetFields(resetFieldsOnChange))
      );
    },
    [field, onTaskChange, resetFieldsOnChange, resetFields]
  );

  // Valid only if:
  // 1. Type is correct (itemNoAndType is not null)
  // 2. Data exists (item was found in the system)
  // 3. New item is different from existing item
  const isSameArticle =
    !isLoading(itemNoAndType) &&
    itemNoAndType !== null &&
    !isLoading(existingItemNoAndType) &&
    existingItemNoAndType !== null &&
    itemNoAndType.no === existingItemNoAndType.no;

  const valid =
    !isLoading(itemNoAndType) &&
    itemNoAndType !== null &&
    !isFetchingItems &&
    items[0] != null &&
    !isSameArticle;

  // Hide fragment if visibility condition not met
  const isVisible =
    !isLoading(task) &&
    shouldShowField(task as Record<string, any>, visibility);

  // Fragment is valid if hidden OR if visible with valid selection
  const fragmentValid = !isVisible || valid;

  useImperativeHandle(ref, () => {
    return { valid: fragmentValid };
  }, [fragmentValid]);

  if (!isVisible) {
    return null;
  }

  if (isLoading(itemNoAndType) || isFetchingItems) {
    return <LoadingCreatorFragment />;
  }

  return (
    <AttachArticle
      item={items[0] ?? null}
      setItemNoAndType={handleItemChange}
      disabled={blockInput || isFetchingItems}
      isError={
        !valid && showValidation && !isSameArticle && itemNoAndType === null
      }
      title={t(title)}
      inlineErrorMessage={
        isSameArticle ? t("pleaseSelectADifferentArticleString") : undefined
      }
    />
  );
}

export default ChangeArticleCreatorFragment;
